import React from 'react'
import { Document, Page } from 'react-pdf'
import type { QuoteFile } from 'paintscout'
import configurePdfWorker from './pdfWorker'

configurePdfWorker()

export interface FileViewerPdfThumbnailProps {
  file: QuoteFile
  /**
   * Rendered page width in px. Matches the widths the legacy Cloudinary `.png`
   * thumb used (50 in the horizontal strip, 64 in the vertical grid) so the
   * canvas drops into the gallery's existing `pdfPreviewSlot` unchanged.
   */
  width: number
  /**
   * Shown while the PDF loads and whenever it fails to load/parse (offline,
   * CORS, non-PDF binary). Callers pass the same generic file icon the gallery
   * has always used, so a migrated S3 PDF is never worse off than before.
   */
  fallback: React.ReactNode
}

/**
 * A tile-sized react-pdf renderer that draws page 1 of a PDF as a gallery
 * thumbnail. Used for S3 PDFs that have no `cloudinaryPublicId` (and therefore
 * no server-derived `.png` thumb) — parity with the old Cloudinary thumbnail
 * (#21643). Mirrors `FileViewerPdfPreview` (same worker config + react-pdf
 * primitives) but strips the paging/zoom chrome down to a single static page.
 *
 * Memoised on `file.src`/`width` (below): react-pdf's `Document` re-fetches and
 * re-parses whenever the `file` prop identity changes, and the gallery hands a
 * fresh `fallback` element on every render, so without this a list interaction
 * (hover/select) would re-download every PDF thumbnail.
 */
function FileViewerPdfThumbnail({ file, width, fallback }: FileViewerPdfThumbnailProps) {
  // Only ever hand an https URL to pdf.js: `src` comes from user-uploaded file
  // records in a multi-tenant app, so a `javascript:`/`data:`/`blob:` value
  // (e.g. an in-flight upload) must not reach the parser. Mirrors the
  // https guard in FileViewerPdfPreview. Anything else falls back to the icon.
  if (!file.src?.startsWith('https://')) {
    return <>{fallback}</>
  }

  return (
    <Document
      file={file.src}
      loading={<>{fallback}</>}
      error={<>{fallback}</>}
      // Surface load/parse failures (CORS misconfig, non-PDF content-type,
      // missing keys) so S3 rollout issues are diagnosable instead of silently
      // degrading to the icon. Mirrors PresentationNew/FilePreview.
      onLoadError={(error) => console.error(`FileViewerPdfThumbnail: failed to load PDF ${file.src}`, error)}
      onSourceError={(error) => console.error(`FileViewerPdfThumbnail: failed to fetch PDF ${file.src}`, error)}
    >
      <Page pageNumber={1} width={width} renderTextLayer={false} renderAnnotationLayer={false} />
    </Document>
  )
}

export default React.memo(
  FileViewerPdfThumbnail,
  (prev, next) => prev.file.src === next.file.src && prev.width === next.width
)
